import { FoundingOfferStrip } from "./shared/FoundingOffer";
import React, { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { useAuth } from "../AuthContext";
import { useToast } from "../ToastContext";
import { useLanguage } from "../i18n";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { decodeGoogleCredential } from "../googleAuth";
import { PasswordInput } from "./PasswordInput";

type Step = "start" | "forgotPassword" | "forgotPasswordVerify" | "verifyEmail" | "role" | "propertyType" | "city" | "bankDetails" | "done";
type Role = "owner" | "tenant";

const STEP_ORDER: Record<Role, Step[]> = {
  owner: ["verifyEmail", "role", "propertyType", "city", "bankDetails", "done"],
  tenant: ["verifyEmail", "role", "done"],
};

export function RegistrationWizard({ onBackToLanguage }: { onBackToLanguage: () => void }) {
  const { login } = useAuth();
  const toast = useToast();
  const { t } = useLanguage();

  const [role, setRole] = useState<Role | null>(null);
  const [step, setStep] = useState<Step>("start");
  const [authTab, setAuthTab] = useState<"login" | "register">("login");
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [city, setCity] = useState("");
  const [accountType, setAccountType] = useState<"independent" | "pg" | "both">("independent");
  const [payoutMethod, setPayoutMethod] = useState<"upi" | "bank">("upi");
  const [upiId, setUpiId] = useState("");
  const [bankAccountHolder, setBankAccountHolder] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankIfsc, setBankIfsc] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pendingAuth, setPendingAuth] = useState<{ token: string; user: any } | null>(null);
  const [founding, setFounding] = useState<{ spot: number; endsAt: string; plan?: string } | null>(null);
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  // Sign-up email check: a code is emailed before the account is created
  const [signupCode, setSignupCode] = useState("");
  const [signupSentTo, setSignupSentTo] = useState("");
  const [signupWait, setSignupWait] = useState(0);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  // Set when someone arrives from Google with an email that has no account yet
  const [googleCredential, setGoogleCredential] = useState("");
  useEffect(() => {
    if (signupWait <= 0) return;
    const id = setTimeout(() => setSignupWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [signupWait]);

  // Picks up ?ref=CODE from a shared referral link (see ReferEarn.tsx) so
  // the field below is prefilled — still editable, and harmless if absent.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      setReferralCode(ref.trim().toUpperCase());
      setAuthTab("register");
    }
  }, []);

  const inSignupFlow = role !== null && step !== "start";
  const order = STEP_ORDER[role ?? "owner"];
  const stepIndex = order.indexOf(step);
  const progress = inSignupFlow ? Math.round(((stepIndex + 1) / order.length) * 100) : 0;

  function goNext(next: Step) {
    setError("");
    setStep(next);
  }
  function goBack() {
    setError("");
    if (step === "start") return onBackToLanguage();
    // Each step's predecessor is fixed regardless of whether `role` has
    // been picked yet — deriving this from STEP_ORDER[role] instead breaks
    // on the very first "role" screen, since `role` is still null there
    // (it's only set once you tap Owner/Tenant, which also navigates away).
    const prevStep: Record<Step, Step | null> = {
      start: null,
      forgotPassword: "start",
      forgotPasswordVerify: "forgotPassword",
      verifyEmail: "start",
      role: "verifyEmail",
      propertyType: "role",
      city: "propertyType",
      bankDetails: "city",
      done: role === "owner" ? "bankDetails" : "role",
    };
    const prev = prevStep[step];
    if (prev) setStep(prev);
  }

  async function submitLogin() {
    if (!email.trim() || !password) return setError(t("auth.errEmailPassword"));
    setBusy(true);
    setError("");
    try {
      const data = await api.post("/api/auth/login", { email: email.trim(), password });
      toast(t("auth.welcomeBackToast", { name: data.user.name.split(" ")[0] }));
      login(data.token, data.user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  async function submitForgotPassword() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError(t("auth.errValidEmail"));
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/forgot-password", { email: email.trim() });
      setResetCode("");
      setResetNewPassword("");
      setResetConfirmPassword("");
      setResetDone(false);
      goNext("forgotPasswordVerify");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  // Same endpoint as the initial send — a fresh code just retires whatever
  // was emailed before (see the server-side comment on /api/auth/forgot-
  // password for why that's safe), so "resend" needs no separate API.
  async function resendResetCode() {
    setResendBusy(true);
    setError("");
    try {
      await api.post("/api/auth/forgot-password", { email: email.trim() });
      toast(t("auth.forgotPasswordResendToast"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setResendBusy(false);
    }
  }

  async function submitResetPassword() {
    if (!/^\d{6}$/.test(resetCode.trim())) return setError(t("auth.errCodeLength"));
    if (resetNewPassword.length < 6) return setError(t("auth.errPasswordLength"));
    if (resetNewPassword !== resetConfirmPassword) return setError(t("auth.errPasswordMismatch"));
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/reset-password", { email: email.trim(), code: resetCode.trim(), newPassword: resetNewPassword });
      setResetDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogleCredential(credential: string) {
    setBusy(true);
    setError("");
    try {
      const data = await api.post("/api/auth/google", { idToken: credential });
      toast(t("auth.welcomeBackToast", { name: data.user.name.split(" ")[0] }));
      login(data.token, data.user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        const decoded = decodeGoogleCredential(credential);
        if (decoded) {
          setEmail(decoded.email);
          setName(decoded.name);
        }
        setGoogleCredential(credential);
        toast(t("auth.googleNoAccountToast"));
        goNext("role");
      } else {
        setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitRegisterTab() {
    if (!name.trim()) return setError(t("auth.errName"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError(t("auth.errValidEmail"));
    if (!/^[6-9]\d{9}$/.test(phone.trim())) return setError(t("auth.errValidPhone"));
    if (password.length < 6) return setError(t("auth.errPasswordLength"));
    if (password !== confirmPassword) return setError(t("auth.errPasswordMismatch"));
    if (!agreedTerms) return setError(t("auth.errMustAgreeTerms"));
    setBusy(true);
    setError("");
    try {
      const { available } = await api.get(`/api/auth/check-email?email=${encodeURIComponent(email.trim())}`);
      if (!available) return setError(t("auth.errEmailTaken"));
    } catch {
      // Check failed (offline, etc.): carry on, the code step below reports any real problem
    }
    try {
      // Already confirmed this exact address earlier in this sign-up? Skip straight on.
      if (verifiedEmail === email.trim().toLowerCase()) {
        goNext("role");
        return;
      }
      await sendSignupCode();
      goNext("verifyEmail");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  async function sendSignupCode() {
    try {
      const r = await api.post("/api/auth/signup-email/send-code", { email: email.trim(), name: name.trim() });
      setSignupSentTo(r?.sentTo ?? email.trim());
      setSignupWait(60);
      setSignupCode("");
    } catch (err) {
      // "wait a minute" means a code was just sent: carry on to the code box
      if (err instanceof ApiError && err.status === 429) {
        setSignupSentTo((s) => s || email.trim());
        return;
      }
      throw err;
    }
  }

  async function resendSignupCode() {
    setBusy(true);
    setError("");
    try {
      await sendSignupCode();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  async function confirmSignupCode() {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/signup-email/verify", { email: email.trim(), code: signupCode });
      setVerifiedEmail(email.trim().toLowerCase());
      toast(t("verifyEmail.verifiedToast"));
      goNext("role");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  function submitPropertyType() {
    goNext("city");
  }

  function submitCity() {
    goNext("bankDetails");
  }

  async function finishRegistration() {
    if (role === "owner") {
      if (payoutMethod === "upi") {
        if (!upiId.trim()) return setError(t("auth.errUpiRequired"));
        if (!/^[A-Za-z0-9.\-_]{2,256}@[A-Za-z]{2,64}$/.test(upiId.trim())) return setError(t("auth.errValidUpi"));
      } else {
        if (!bankAccountHolder.trim() || !bankAccountNumber.trim() || !bankIfsc.trim()) return setError(t("auth.errBankDetailsRequired"));
        if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(bankIfsc.trim())) return setError(t("auth.errValidIfsc"));
      }
    }
    setBusy(true);
    setError("");
    try {
      const data = await api.post("/api/auth/register", {
        email: email.trim(),
        password,
        phone: phone || undefined,
        role,
        name,
        city: city || undefined,
        accountType: role === "owner" ? accountType : undefined,
        upiId: role === "owner" ? upiId || undefined : undefined,
        bankAccountHolder: role === "owner" ? bankAccountHolder || undefined : undefined,
        bankAccountNumber: role === "owner" ? bankAccountNumber || undefined : undefined,
        bankIfsc: role === "owner" ? bankIfsc || undefined : undefined,
        referralCode: referralCode.trim() || undefined,
        emailVerified: verifiedEmail === email.trim().toLowerCase() ? true : undefined,
        googleIdToken: googleCredential && !verifiedEmail ? googleCredential : undefined,
      });
      setPendingAuth({ token: data.token, user: data.user });
      setFounding(data.foundingOffer ?? null);
      goNext("done");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center-col">
      <div style={{ flex: "0 0 auto", padding: "16px 18px 12px", display: "flex", alignItems: "center", gap: 12 }}>
        <button
          className="icon-btn"
          style={{ visibility: step === "done" ? "hidden" : "visible" }}
          onClick={goBack}
          aria-label="Back"
        >
          ‹
        </button>
        <div style={{ flex: 1, height: 5, borderRadius: 99, background: "var(--brand-100)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "var(--brand-700)", borderRadius: 99, transition: "width .3s ease" }} />
        </div>
      </div>

      <div className="content" style={{ paddingTop: 8 }}>
        {error && <div className="error-banner">{error}</div>}

        {step === "start" && (
          <>
            <img src="/logo.svg" alt="ToLetBook" className="reg-logo" />
            <div className="reg-eyebrow">{t("auth.welcomeEyebrow")}</div>
            <div className="reg-title">{authTab === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}</div>
            <div className="reg-sub">{authTab === "login" ? t("auth.loginSub") : t("auth.registerSub")}</div>

            <div className="card">
              <div className="seg-control">
                <button
                  type="button"
                  className={`seg-btn${authTab === "login" ? " active" : ""}`}
                  onClick={() => setAuthTab("login")}
                >
                  {t("auth.logIn")}
                </button>
                <button
                  type="button"
                  className={`seg-btn${authTab === "register" ? " active" : ""}`}
                  onClick={() => setAuthTab("register")}
                >
                  {t("auth.createAccount")}
                </button>
              </div>

              {authTab === "register" && (
                <div className="field">
                  <label>{t("auth.fullNameLabel")}</label>
                  <div className="input-wrap">
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.fullNamePlaceholder")} />
                  </div>
                </div>
              )}

              <div className="field">
                <label>{t("auth.emailLabel")}</label>
                <div className="input-wrap">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.emailPlaceholder")}
                  />
                </div>
              </div>

              {authTab === "register" && (
                <div className="field">
                  <label>{t("auth.phoneLabel")}</label>
                  <div className="input-wrap">
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder={t("auth.phonePlaceholder")}
                    />
                  </div>
                </div>
              )}

              <div className="field">
                <label>{t("auth.passwordLabel")}</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={authTab === "login" ? t("auth.passwordPlaceholder") : t("auth.newPasswordPlaceholder")}
                  onKeyDown={(e) => e.key === "Enter" && (authTab === "login" ? submitLogin() : submitRegisterTab())}
                />
                {authTab === "register" && <div className="field-hint">{t("auth.passwordHint")}</div>}
              </div>

              {authTab === "register" && (
                <div className="field">
                  <label>{t("auth.confirmPasswordLabel")}</label>
                  <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("auth.newPasswordPlaceholder")}
                    onKeyDown={(e) => e.key === "Enter" && submitRegisterTab()}
                  />
                </div>
              )}

              {authTab === "register" && (
                <div className="field">
                  <label>{t("auth.referralCodeLabel")}</label>
                  <div className="input-wrap">
                    <input
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder={t("auth.referralCodePlaceholder")}
                    />
                  </div>
                </div>
              )}

              {authTab === "register" ? (
                <div style={{ margin: "4px 0 16px" }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} style={{ marginTop: 3 }} />
                    <span className="sub">{t("auth.agreeTermsText")}</span>
                  </label>
                  <div style={{ display: "flex", gap: 14, marginTop: 4, marginLeft: 26 }}>
                    <a className="link-btn" href="/terms.html" target="_blank" rel="noopener">
                      {t("auth.viewTermsLink")}
                    </a>
                    <a className="link-btn" href="/privacy.html" target="_blank" rel="noopener">
                      {t("auth.viewPrivacyLink")}
                    </a>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -8, marginBottom: 14 }}>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => goNext("forgotPassword")}
                  >
                    {t("auth.forgotPasswordLink")}
                  </button>
                </div>
              )}

              <button className="btn-solid" disabled={busy} onClick={authTab === "login" ? submitLogin : submitRegisterTab}>
                {authTab === "login" ? (busy ? t("auth.loginBusy") : t("auth.logIn")) : t("common.continue")}
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0 6px", color: "var(--ink-faint)", fontSize: 12, fontWeight: 700 }}>
                <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
                {t("auth.orDivider")}
                <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
              </div>
              <GoogleSignInButton onCredential={handleGoogleCredential} />
            </div>
          </>
        )}

        {step === "forgotPassword" && (
          <>
            <div className="reg-eyebrow">{t("auth.forgotPasswordEyebrow")}</div>
            <div className="reg-title">{t("auth.forgotPasswordTitle")}</div>
            <div className="reg-sub">{t("auth.forgotPasswordSub")}</div>
            {error && <div className="error-banner">{error}</div>}
            <div className="field">
              <label>{t("auth.emailLabel")}</label>
              <div className="input-wrap">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.emailPlaceholder")}
                  onKeyDown={(e) => e.key === "Enter" && submitForgotPassword()}
                />
              </div>
            </div>
            <button className="btn-solid" disabled={busy} onClick={submitForgotPassword}>
              {busy ? t("auth.forgotPasswordBusy") : t("auth.forgotPasswordSubmit")}
            </button>
          </>
        )}

        {step === "forgotPasswordVerify" && (
          <>
            {resetDone ? (
              <>
                <div style={{ fontSize: 44, marginBottom: 14, textAlign: "center" }}>✓</div>
                <div className="reg-title" style={{ textAlign: "center" }}>{t("auth.resetPasswordDoneTitle")}</div>
                <div className="reg-sub" style={{ textAlign: "center" }}>{t("auth.resetPasswordDoneSub")}</div>
                <button
                  className="btn-solid"
                  onClick={() => {
                    setAuthTab("login");
                    goNext("start");
                  }}
                >
                  {t("auth.logIn")}
                </button>
              </>
            ) : (
              <>
                <div className="reg-eyebrow">{t("auth.resetPasswordEyebrow")}</div>
                <div className="reg-title">{t("auth.forgotPasswordSentTitle")}</div>
                <div className="reg-sub">{t("auth.forgotPasswordSentSub")}</div>
                {error && <div className="error-banner">{error}</div>}
                <div className="field">
                  <label>{t("auth.forgotPasswordCodeLabel")}</label>
                  <div className="input-wrap">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder={t("auth.forgotPasswordCodePlaceholder")}
                    />
                  </div>
                  <div className="sub" style={{ marginTop: 4 }}>{t("auth.resetPasswordSub")}</div>
                </div>
                <div className="field">
                  <label>{t("auth.newPasswordLabel")}</label>
                  <PasswordInput
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    placeholder={t("auth.newPasswordPlaceholder")}
                  />
                </div>
                <div className="field">
                  <label>{t("auth.confirmPasswordLabel")}</label>
                  <PasswordInput
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    placeholder={t("auth.newPasswordPlaceholder")}
                    onKeyDown={(e) => e.key === "Enter" && submitResetPassword()}
                  />
                </div>
                <button className="btn-solid" disabled={busy} onClick={submitResetPassword}>
                  {busy ? t("auth.resetPasswordBusy") : t("auth.resetPasswordSubmit")}
                </button>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
                  <button type="button" className="link-btn" onClick={() => goNext("forgotPassword")}>
                    {t("auth.forgotPasswordChangeEmailLink")}
                  </button>
                  <button type="button" className="link-btn" disabled={resendBusy} onClick={resendResetCode}>
                    {t("auth.forgotPasswordResendLink")}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {step === "verifyEmail" && (
          <>
            <div className="reg-eyebrow">{t("verifyEmail.signupEyebrow")}</div>
            <div className="reg-title">{t("verifyEmail.signupTitle")}</div>
            <div className="reg-sub">{t("verifyEmail.sentTo", { email: signupSentTo || email.trim() })}</div>
            <div className="field">
              <div className="input-wrap">
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={signupCode}
                  onChange={(e) => setSignupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="------"
                  style={{ textAlign: "center", fontSize: 28, letterSpacing: "0.4em", fontWeight: 700 }}
                />
              </div>
            </div>
            <button className="btn-solid" disabled={signupCode.length !== 6 || busy} onClick={confirmSignupCode}>
              {busy ? t("verifyEmail.checking") : t("verifyEmail.confirmBtn")}
            </button>
            <div style={{ textAlign: "center" }}>
              <button type="button" className="link-btn" style={{ marginTop: 12 }} disabled={signupWait > 0 || busy} onClick={resendSignupCode}>
                {signupWait > 0 ? t("verifyEmail.resendIn", { seconds: signupWait }) : t("verifyEmail.resendBtn")}
              </button>
            </div>
            <div style={{ textAlign: "center" }}>
              <button type="button" className="link-btn" style={{ marginTop: 6 }} onClick={() => goNext("start")}>
                {t("verifyEmail.changeEmail")}
              </button>
            </div>
          </>
        )}

        {step === "role" && (
          <>
            <div className="reg-eyebrow">{t("auth.getStartedEyebrow")}</div>
            <div className="reg-title">{t("auth.roleTitle")}</div>
            <div className="reg-sub">{t("auth.roleSub")}</div>
            <div
              className={`role-card${role === "owner" ? " selected" : ""}`}
              onClick={() => setRole("owner")}
            >
              <b>{t("auth.roleOwnerTitle")}</b>
              <span>{t("auth.roleOwnerSub")}</span>
            </div>
            <div
              className={`role-card${role === "tenant" ? " selected" : ""}`}
              onClick={() => setRole("tenant")}
            >
              <b>{t("auth.roleTenantTitle")}</b>
              <span>{t("auth.roleTenantSub")}</span>
            </div>
            {googleCredential && !verifiedEmail && (
              <div className="sub" style={{ marginBottom: 10 }}>{t("auth.googleNoAccountNotice", { email })}</div>
            )}
            {role === "owner" && <FoundingOfferStrip compact />}
            {/* Google sign-in skips straight to this screen without ever
                visiting "start", where phone is normally collected — ask
                for it here instead, only when it's still missing. */}
            {!phone && (
              <div className="field" style={{ marginTop: 4 }}>
                <label>{t("auth.phoneLabel")}</label>
                <div className="input-wrap">
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder={t("auth.phonePlaceholder")}
                  />
                </div>
              </div>
            )}
            <button
              className="btn-solid"
              disabled={!role || busy}
              onClick={() => {
                if (!/^[6-9]\d{9}$/.test(phone.trim())) return setError(t("auth.errValidPhone"));
                role === "owner" ? goNext("propertyType") : finishRegistration();
              }}
            >
              {busy ? t("auth.finishBusy") : t("common.continue")}
            </button>
          </>
        )}

        {step === "propertyType" && (
          <>
            <div className="reg-eyebrow">{t("auth.step3Eyebrow")}</div>
            <div className="reg-title">{t("auth.propertyTypeTitle")}</div>
            <div className="reg-sub">{t("auth.propertyTypeSub")}</div>
            {([
              ["independent", t("auth.typeIndependentTitle"), t("auth.typeIndependentSub")],
              ["pg", t("auth.typePgTitle"), t("auth.typePgSub")],
              ["both", t("auth.typeBothTitle"), t("auth.typeBothSub")],
            ] as const).map(([val, title, sub]) => (
              <div
                key={val}
                className={`role-card${accountType === val ? " selected" : ""}`}
                onClick={() => setAccountType(val)}
              >
                <b>{title}</b>
                <span>{sub}</span>
              </div>
            ))}
            <button className="btn-solid" disabled={busy} onClick={submitPropertyType}>
              {t("common.continue")}
            </button>
          </>
        )}

        {step === "city" && (
          <>
            <div className="reg-eyebrow">{t("auth.almostDoneEyebrow")}</div>
            <div className="reg-title">{t("auth.cityTitle")}</div>
            <div className="reg-sub">{t("auth.citySub")}</div>
            <div className="field">
              <label>{t("auth.cityLabel")}</label>
              <div className="input-wrap">
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("auth.cityPlaceholder")} />
              </div>
            </div>
            <button className="btn-solid" disabled={busy} onClick={submitCity}>
              {t("common.continue")}
            </button>
          </>
        )}

        {step === "bankDetails" && (
          <>
            <div className="reg-eyebrow">{t("auth.almostDoneEyebrow")}</div>
            <div className="reg-title">{t("auth.bankDetailsTitle")}</div>
            <div className="reg-sub">{t("auth.bankDetailsSub")}</div>

            <div className="chip-row">
              <div className={`chip${payoutMethod === "upi" ? " selected" : ""}`} onClick={() => setPayoutMethod("upi")}>
                {t("auth.payoutMethodUpi")}
              </div>
              <div className={`chip${payoutMethod === "bank" ? " selected" : ""}`} onClick={() => setPayoutMethod("bank")}>
                {t("auth.payoutMethodBank")}
              </div>
            </div>

            {payoutMethod === "upi" ? (
              <div className="field">
                <label>{t("auth.upiIdLabel")}</label>
                <div className="input-wrap">
                  <input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder={t("auth.upiIdPlaceholder")} />
                </div>
                <div className="field-hint">{t("auth.bankDetailsHint")}</div>
              </div>
            ) : (
              <>
                <div className="field">
                  <label>{t("auth.bankAccountHolderLabel")}</label>
                  <div className="input-wrap">
                    <input value={bankAccountHolder} onChange={(e) => setBankAccountHolder(e.target.value)} placeholder={t("auth.bankAccountHolderPlaceholder")} />
                  </div>
                </div>
                <div className="field">
                  <label>{t("auth.bankAccountNumberLabel")}</label>
                  <div className="input-wrap">
                    <input
                      inputMode="numeric"
                      value={bankAccountNumber}
                      onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 18))}
                      placeholder={t("auth.bankAccountNumberPlaceholder")}
                    />
                  </div>
                </div>
                <div className="field">
                  <label>{t("auth.bankIfscLabel")}</label>
                  <div className="input-wrap">
                    <input
                      value={bankIfsc}
                      onChange={(e) => setBankIfsc(e.target.value.toUpperCase().slice(0, 11))}
                      placeholder={t("auth.bankIfscPlaceholder")}
                    />
                  </div>
                  <div className="field-hint">{t("auth.bankDetailsHint")}</div>
                </div>
              </>
            )}

            <button className="btn-solid" disabled={busy} onClick={finishRegistration}>
              {busy ? t("auth.finishBusy") : t("auth.finish")}
            </button>
          </>
        )}

        {step === "done" && (
          <div style={{ textAlign: "center", paddingTop: 40 }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>✓</div>
            <div className="reg-title">{t("auth.doneTitle")}</div>
            <div className="reg-sub">{t("auth.doneSub")}</div>
            {founding && (
              <div style={{ margin: "14px 0", padding: "12px 14px", borderRadius: 14, border: "1.5px solid #f2965c", background: "rgba(242,150,92,.12)" }}>
                <b>{t("foundingOffer.doneTitle", { spot: founding.spot })}</b>
                <div className="sub" style={{ marginTop: 2 }}>
                  {t("foundingOffer.doneBody", { plan: founding.plan === "premium" ? "Premium" : "Pro", date: new Date(founding.endsAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) })}
                </div>
              </div>
            )}
            <button
              className="btn-solid"
              onClick={() => {
                if (pendingAuth) login(pendingAuth.token, pendingAuth.user);
                toast(t("auth.welcomeNewToast", { name: name.split(" ")[0] }));
              }}
            >
              {t("auth.goToDashboard")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
